import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

export default function Navbar() {
  const { t, i18n } = useTranslation()
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const href = e.currentTarget.getAttribute('href')
    if (!href?.startsWith('#')) return

    const targetId = href.replace('#', '')
    const element = document.getElementById(targetId)
    if (element) {
      setIsMobileMenuOpen(false)
      const navHeight = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - navHeight

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  const navLinks = [
    { href: '#presentation', label: t('nav.about') },
    { href: '#features', label: t('nav.features') },
    { href: '#pricing', label: t('nav.plans') },
    { href: '#contact', label: t('nav.contact') }
  ]

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src={isScrolled ? '/images/logo_completo.png' : '/images/logo_blanco.png'}
                alt={t('app.name')}
                className="h-12 transition-opacity duration-300"
              />
            </Link>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={handleNavClick}
                  className={`text-sm font-medium transition-colors hover:text-primary-600 ${
                    isScrolled ? 'text-gray-800' : 'text-white'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className={`flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary-600 ${
                    isScrolled ? 'text-gray-800' : 'text-white'
                  }`}
                >
                  <span>{t(`app.languages.${i18n.language}`)}</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={isLanguageMenuOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                    />
                  </svg>
                </button>

                {/* Language Menu */}
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 py-2 w-32 bg-white rounded-lg shadow-xl">
                    {Object.keys(t('app.languages', { returnObjects: true })).map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          i18n.changeLanguage(lang)
                          setIsLanguageMenuOpen(false)
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${i18n.language === lang ? 'text-primary-600' : 'text-gray-800'}`}
                      >
                        {t(`app.languages.${lang}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <svg
                className={`w-6 h-6 ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div
          className={`fixed right-0 top-0 h-full w-64 bg-white transform transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4">
            <button
              className="absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="mt-8 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={handleNavClick}
                  className="block py-2 text-gray-800 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Link 
                to="/privacy"
                className="block py-2 text-gray-800 hover:text-primary-600 transition-colors"
              >
                {t('navigation.privacy')}
              </Link>
              <Link 
                to="/cookies"
                className="block py-2 text-gray-800 hover:text-primary-600 transition-colors"
              >
                {t('navigation.cookies')}
              </Link>

              {/* Language Options */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-600 mb-2">Idioma / Language</p>
                {Object.keys(t('app.languages', { returnObjects: true })).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      i18n.changeLanguage(lang)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`block w-full text-left py-2 text-sm ${i18n.language === lang ? 'text-primary-600' : 'text-gray-800'} hover:text-primary-600 transition-colors`}
                  >
                    {t(`app.languages.${lang}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
